import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetAssetsAvailabilityDataQuery } from '../../../api/booking/bookingApiSlice';
import Permission from '../../../common/permissions';
import CustomButton from '../../../components/CustomButton';
import { getSlotBookingTarget } from '../../../config/bookingServiceMapping';
import { makeHasPermissionSelector, tenantSelector } from '../../../selector/authenticationSelector';
import { locationListSelector, locationTimeZoneIn } from '../../../selector/locationsSelector';
import type { RootState } from '../../../store/store';
import { cn } from '../../../utils/utils';
import { ROOM_PRODUCT_USE_CASES } from '../../services/serviceConfigurationRules';
import { buildSlotPrefillSearch } from '../../rentals/CreateBooking/slotPrefill';
import { OCCUPANCY_HATCH } from '../components/occupancyHatch';
import { GRID_TZ } from '../components/utilizationRange';
import RoomWeekGrid, { PAUSED_WINDOW_OUTLINE, WINDOW_OUTLINE, WeekAnchor, WeekRoom } from './RoomWeekGrid';
import {
    WeekSlot,
    selectionRange,
    shiftWeek,
    weekStartFor,
    zonedDateKey,
} from './roomWeek';

// The operator week calendar — module M12 of
// docs/plans/serial-use-and-sports-hall-modules.md (§2, ticket #3039): one or
// several rooms, a week with hours, and a free slot booked straight from the
// grid — object and time prefilled on the create page.
//
// Which objects it shows is a question about the occupancy data, not about a
// use case: the occupancy endpoint answers per group, and only where the
// group IS the object (`ROOM_PRODUCT_USE_CASES`, ADR-0025) is a group's week
// that object's week. Pooled objects (rental, SUP, storage) would show "2 of
// 10 taken", which is utilization, not a calendar — their breadth here is
// "later" (plan §2, M12 row: space now · thing later · person later).
//
// Which create page a slot opens, and whether its form can take the slot,
// is asked of `getSlotBookingTarget`, i.e. of the create pages that read a
// slot prefill — not of a use-case list.
//
// State lives in the URL (`view=week&week=YYYY-MM-DD&rooms=…`), so a link to
// a week of particular rooms survives a refresh and can be sent on.
//
// Zones (#3219): the week the calendar NAMES — its key, label and navigation —
// is a calendar week in the station default, the frame shared by rooms of
// different locations. Each room DRAWS that week on its own location's clock
// (`WeekRoom.timeZone`), and a slot picked there is prefilled on that clock.

// How many rooms the calendar opens with, and how many each "show more" adds
// (PO, 2026-10-03): a small municipality sees all its halls at once, a large
// tenant does not fire forty occupancy requests on arrival.
const ROOMS_PER_STEP = 8;

const WEEK_PARAMS = {
    week: 'week',
    rooms: 'rooms',
} as const;

interface RoomWeekCalendarProps {
    searchQuery?: string;
    allowedUseCases?: string[];
}

const isRoomObject = (useCases: string[] | undefined): boolean =>
    (useCases ?? []).some((useCase) => ROOM_PRODUCT_USE_CASES.has(useCase.toLowerCase()));

const RoomWeekCalendar: React.FC<RoomWeekCalendarProps> = ({ searchQuery = '', allowedUseCases }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const config = useSelector((state: RootState) => state.authentication.config);
    const tenant = useSelector(tenantSelector);
    const locations = useSelector(locationListSelector);
    const canBook = useSelector(makeHasPermissionSelector(Permission.createBookings()));

    const now = new Date();
    const weekParam = searchParams.get(WEEK_PARAMS.week);
    const weekStart = useMemo(() => weekStartFor(weekParam, new Date()), [weekParam]);
    const weekKey = zonedDateKey(weekStart);

    // The object list. The statistics endpoint is month-keyed; the objects it
    // lists do not depend on the month, and asking for the week's own month
    // shares the cache entry with the month view.
    const [year, month] = weekKey.split('-');
    const { data: assets, isLoading, isError } = useGetAssetsAvailabilityDataQuery({
        data: { year: year ?? '', month: month ?? '' },
    });

    const { rooms, withoutProduct } = useMemo(() => {
        const services: string[] = config?.services ?? [];
        const createOptions: string[] = config?.createBookingOptions ?? [];
        const query = searchQuery.trim().toLowerCase();
        const candidates = (assets ?? [])
            .filter((asset) => isRoomObject(asset.useCases))
            .filter((asset) => !allowedUseCases
                || asset.useCases.some((useCase) => allowedUseCases.includes(useCase)))
            .filter((asset) => !query
                || asset.assetName.toLowerCase().includes(query)
                || (asset.locationName ?? '').toLowerCase().includes(query));
        const list: WeekRoom[] = candidates
            .flatMap((asset) => (asset.productId ? [{
                assetId: asset.assetId,
                groupId: asset.productId,
                locationId: asset.locationId,
                // #3219: each room's week is drawn on its own location's clock.
                timeZone: locationTimeZoneIn(locations, asset.locationId),
                name: asset.assetName,
                subtitle: asset.locationName,
                booking: getSlotBookingTarget(asset.useCases, services, createOptions),
            }] : []))
            .sort((a, b) => a.name.localeCompare(b.name, i18n.language));
        return { rooms: list, withoutProduct: candidates.length - list.length };
    }, [assets, allowedUseCases, searchQuery, config, i18n.language, locations]);

    // Selected rooms from the URL; ids from another tenant or a deleted room
    // are dropped. None selected → the first ROOMS_PER_STEP rooms, so the view
    // is never blank and a handful of halls is compared at a glance.
    const selectedIds = useMemo(() => {
        const known = new Set(rooms.map((room) => room.assetId));
        const fromUrl = (searchParams.get(WEEK_PARAMS.rooms) ?? '')
            .split(',')
            .filter((id) => known.has(id));
        if (fromUrl.length > 0) return fromUrl;
        return rooms.slice(0, ROOMS_PER_STEP).map((room) => room.assetId);
    }, [rooms, searchParams]);
    const selectedRooms = rooms.filter((room) => selectedIds.includes(room.assetId));

    const updateParams = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value === null) next.delete(key);
            else next.set(key, value);
        });
        setSearchParams(next);
    };

    const hiddenRooms = rooms.filter((room) => !selectedIds.includes(room.assetId));
    const showMoreRooms = () => {
        const next = [
            ...selectedIds,
            ...hiddenRooms.slice(0, ROOMS_PER_STEP).map((room) => room.assetId),
        ];
        updateParams({ [WEEK_PARAMS.rooms]: next.join(',') });
    };
    const anyBookable = selectedRooms.some((room) => room.booking !== null);

    const toggleRoom = (assetId: string) => {
        const next = selectedIds.includes(assetId)
            ? selectedIds.filter((id) => id !== assetId)
            : [...selectedIds, assetId];
        updateParams({ [WEEK_PARAMS.rooms]: next.length > 0 ? next.join(',') : null });
    };

    // Picking: first click anchors, second click ends the range and opens the
    // create page. The same cell twice books that one hour.
    const [anchor, setAnchor] = useState<WeekAnchor | null>(null);
    const [hovered, setHovered] = useState<WeekAnchor | null>(null);
    const [previewSlots, setPreviewSlots] = useState<WeekSlot[]>([]);
    // Why the last pick could not open the form — two causes, two messages
    // (a refusal must say which).
    const [blocked, setBlocked] = useState<'range' | 'form' | null>(null);

    // A pick belongs to one tenant and one week.
    useEffect(() => {
        setAnchor(null);
        setHovered(null);
        setBlocked(null);
    }, [tenant, weekKey]);

    useEffect(() => {
        if (!anchor) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setAnchor(null);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [anchor]);

    const preview = useMemo(() => {
        if (!anchor || !hovered || hovered.assetId !== anchor.assetId) return new Set<string>();
        const range = selectionRange(previewSlots, anchor.from, hovered.from, new Date());
        if (!range) return new Set<string>();
        const start = new Date(range.from).getTime();
        const end = new Date(range.to).getTime();
        return new Set(previewSlots
            .filter((slot) => {
                const at = new Date(slot.from).getTime();
                return at >= start && at < end;
            })
            .map((slot) => slot.from));
    }, [anchor, hovered, previewSlots]);

    const onHover = (room: WeekRoom, slot: WeekSlot | null, slots: WeekSlot[]) => {
        setHovered(slot ? { assetId: room.assetId, from: slot.from } : null);
        setPreviewSlots(slots);
    };

    const onPick = (room: WeekRoom, slot: WeekSlot, slots: WeekSlot[]) => {
        setBlocked(null);
        if (!anchor || anchor.assetId !== room.assetId) {
            setAnchor({ assetId: room.assetId, from: slot.from });
            return;
        }
        const range = selectionRange(slots, anchor.from, slot.from, new Date());
        if (!range || !room.booking) {
            setBlocked('range');
            return;
        }
        if (!room.booking.fits(range.from, range.to, room.timeZone)) {
            setBlocked('form');
            return;
        }
        navigate(`${room.booking.route}?${buildSlotPrefillSearch({
            assetId: room.assetId,
            locationId: room.locationId,
            from: range.from,
            to: range.to,
        })}`);
    };

    const dateLocale = i18n.language.startsWith('de') ? 'de-DE' : 'en-GB';
    const weekLabel = useMemo(() => {
        const last = new Date(shiftWeek(weekStart, 1).getTime() - 1);
        const format = (date: Date) => date.toLocaleDateString(dateLocale, {
            day: '2-digit', month: '2-digit', year: 'numeric', timeZone: GRID_TZ,
        });
        return `${format(weekStart)} – ${format(last)}`;
    }, [weekStart, dateLocale]);

    // The picked start on the clock of the room it was picked in (#3219).
    const anchorZone = rooms.find((room) => room.assetId === anchor?.assetId)?.timeZone ?? GRID_TZ;
    const anchorLabel = anchor
        ? new Date(anchor.from).toLocaleString(dateLocale, {
            weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            timeZone: anchorZone,
        })
        : '';

    const isCurrentWeek = weekKey === zonedDateKey(weekStartFor(null, now));

    if (isLoading) {
        return <p className={'text-sm text-foreground/40 italic'}>{t('common.loading')}</p>;
    }
    if (isError) {
        return <p className={'text-sm text-foreground/60'}>{t('dashboard.assets_availability.error')}</p>;
    }

    return (
        <div className={'flex flex-col gap-4'}>
            <div className={'flex flex-wrap items-center justify-between gap-3'}>
                <div className={'flex items-center gap-2'}>
                    <CustomButton
                        title={t('dashboard.assets_availability.week_view.previous_week')}
                        isFilled={false}
                        onClick={() => updateParams({ [WEEK_PARAMS.week]: zonedDateKey(shiftWeek(weekStart, -1)) })}
                    />
                    <CustomButton
                        title={t('dashboard.assets_availability.week_view.this_week')}
                        disabled={isCurrentWeek}
                        onClick={() => updateParams({ [WEEK_PARAMS.week]: null })}
                    />
                    <CustomButton
                        title={t('dashboard.assets_availability.week_view.next_week')}
                        isFilled={false}
                        onClick={() => updateParams({ [WEEK_PARAMS.week]: zonedDateKey(shiftWeek(weekStart, 1)) })}
                    />
                </div>
                <p className={'text-sm font-medium text-foreground'}>{weekLabel}</p>
            </div>

            {rooms.length === 0 ? (
                <p className={'text-sm text-foreground/60 py-8 text-center'}>
                    {searchQuery.trim()
                        ? t('dashboard.assets_availability.week_view.no_rooms_match')
                        : t('dashboard.assets_availability.week_view.no_rooms')}
                </p>
            ) : (
                <div>
                    <p className={'text-xs text-foreground/60 mb-2'}>
                        {t('dashboard.assets_availability.week_view.rooms_label')}
                    </p>
                    <div className={'flex flex-wrap gap-2'}>
                        {rooms.map((room) => {
                            const active = selectedIds.includes(room.assetId);
                            return (
                                <button
                                    key={room.assetId}
                                    type={'button'}
                                    aria-pressed={active}
                                    onClick={() => toggleRoom(room.assetId)}
                                    className={cn(
                                        'rounded-full px-3 py-1 text-xs transition-colors',
                                        active
                                            ? 'bg-primary text-text-buttonContrast'
                                            : 'bg-foreground/5 text-foreground/70 hover:text-foreground',
                                    )}
                                >
                                    {room.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {withoutProduct > 0 && (
                <p className={'text-xs text-foreground/60'}>
                    {t('dashboard.assets_availability.week_view.rooms_without_product', { count: withoutProduct })}
                </p>
            )}

            {selectedRooms.length > 0 && (
                <div className={'flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground/60'} role={'status'}>
                    {!canBook && <span>{t('dashboard.assets_availability.week_view.hint_read_only')}</span>}
                    {canBook && anyBookable && !anchor && <span>{t('dashboard.assets_availability.week_view.hint_pick_start')}</span>}
                    {canBook && anchor && (
                        <>
                            <span className={'text-foreground'}>
                                {t('dashboard.assets_availability.week_view.hint_pick_end', { start: anchorLabel })}
                            </span>
                            <button
                                type={'button'}
                                onClick={() => setAnchor(null)}
                                className={'text-primary hover:underline'}
                            >
                                {t('dashboard.assets_availability.week_view.cancel_pick')}
                            </button>
                        </>
                    )}
                    {blocked === 'form' && (
                        <span className={'text-foreground'}>
                            {t('dashboard.assets_availability.week_view.range_outside_form')}
                        </span>
                    )}
                    {blocked === 'range' && (
                        <span className={'text-foreground'}>
                            {t('dashboard.assets_availability.week_view.range_blocked')}
                        </span>
                    )}
                </div>
            )}

            {selectedRooms.map((room) => (
                <RoomWeekGrid
                    key={room.assetId}
                    room={room}
                    weekKey={weekKey}
                    now={now}
                    canBook={canBook}
                    anchor={anchor}
                    preview={preview}
                    onPick={onPick}
                    onHover={onHover}
                />
            ))}

            {selectedRooms.length > 0 && hiddenRooms.length > 0 && (
                <div>
                    <CustomButton
                        title={t('dashboard.assets_availability.week_view.show_more', {
                            count: hiddenRooms.length,
                        })}
                        isFilled={false}
                        onClick={showMoreRooms}
                    />
                </div>
            )}

            {selectedRooms.length > 0 && (
                <div className={'flex flex-wrap items-center gap-4 text-xs text-foreground/60'}>
                    <span className={'flex items-center gap-[6px]'}>
                        <span className={'h-3 w-5 rounded-[2px] bg-foreground/5'} />
                        {t('dashboard.assets_availability.week_view.legend.free')}
                    </span>
                    <span className={'flex items-center gap-[6px]'}>
                        <span className={'h-3 w-5 rounded-[2px] bg-primary'} />
                        {t('dashboard.assets_availability.week_view.legend.taken')}
                    </span>
                    <span className={'flex items-center gap-[6px]'}>
                        <span className={'h-3 w-5 rounded-[2px] bg-primary'} style={OCCUPANCY_HATCH} />
                        {t('asset_detail.utilization.legend.reserved')}
                    </span>
                    <span className={'flex items-center gap-[6px]'}>
                        <span className={'h-3 w-5 rounded-[2px] bg-foreground/5 opacity-40'} />
                        {t('dashboard.assets_availability.week_view.legend.past')}
                    </span>
                    <span className={'flex items-center gap-[6px]'}>
                        <span className={cn('h-3 w-5 rounded-[2px] bg-foreground/5', WINDOW_OUTLINE)} />
                        {t('dashboard.assets_availability.week_view.legend.window')}
                    </span>
                    <span className={'flex items-center gap-[6px]'}>
                        <span className={cn('h-3 w-5 rounded-[2px] bg-foreground/5', PAUSED_WINDOW_OUTLINE)} />
                        {t('dashboard.assets_availability.week_view.legend.window_paused')}
                    </span>
                </div>
            )}

            {/* Interim, until the divided-hall rule lives once in new-backend/admin
                (docs/plans/series-booking-module.md §4.1a): the occupancy endpoint
                counts each room on its own, so a divided room's whole and its parts do
                not yet block each other here. Worded like the divide-room feature
                (`services.divide_room.*`: room, parts, whole room). Said on every week view rather than
                detected per room, because detecting "part of a divided hall" would
                itself be one more copy of the rule. Remove with §4.1a. */}
            {selectedRooms.length > 0 && (
                <p className={'text-xs text-foreground/60'}>
                    {t('dashboard.assets_availability.week_view.divided_room_note')}
                </p>
            )}
        </div>
    );
};

export default RoomWeekCalendar;
